import { ActionTree, GetterTree, MutationTree } from "vuex";

import UserService from "../../services";
import { UserState, CheckPhoneType } from "../../types/UserState";
import { ZipeCodeResponseType } from "../../types/ZipCodeEligibilityTypes";

const state: UserState = {
  token: "",
  roles: [],
  isAuthenticated: false,
  context: "",
  currentUserId: null,
  hasCompletedProfile: false,
  userRolesById: null,
  name: "",
  email: "",
  password: "",
  cpf: "",
  zipCode: "",
  street: "",
  number: "",
  district: "",
  city: "",
  uf: "",
  complement: "",
  phone: "",
  isShowPortability: false,
};

const getters: GetterTree<UserState, any> = {
  name: (st) => st.name,
  email: (st) => st.email,
  password: (st) => st.password,
  cpf: (st) => st.cpf,
  zipCode: (st) => st.zipCode,
  street: (st) => st.street,
  number: (st) => st.number,
  district: (st) => st.district,
  city: (st) => st.city,
  uf: (st) => st.uf,
  complement: (st) => st.complement,
  phone: (st) => st.phone,
  isShowPortability: (st) => st.isShowPortability,
};

const mutations: MutationTree<UserState> = {
  setNameEmail(st: UserState, { name, email, password }) {
    st.name = name;
    st.email = email;
    st.password = password;
  },

  setCpf(st: UserState, { cpf }) {
    st.cpf = cpf;
  },

  setPhone(st: UserState, { phone }) {
    st.phone = phone;
  },

  setIsShowPortability(st: UserState, isShowPortability) {
    st.isShowPortability = isShowPortability;
  },

  setAddress(
    st: UserState,
    { zipCode, street, number, complement, district, city, uf }
  ) {
    st.zipCode = zipCode;
    st.street = street;
    st.number = number;
    st.complement = complement;
    st.district = district;
    st.city = city;
    st.uf = uf;
  },
};

const actions: ActionTree<UserState, any> = {
  async setNameEmail({ commit }, { name, email, password }) {
    commit("setNameEmail", { name, email, password });
  },

  async setCpf({ commit }, { cpf }) {
    commit("setCpf", { cpf });
  },

  async setIsShowPortability({ commit }, showPortability) {
    commit("setIsShowPortability", showPortability);
  },

  async setAddress(
    { commit },
    { zipCode, street, number, complement, district, city, uf }
  ) {
    commit("setAddress", {
      zipCode,
      street,
      number,
      complement,
      district,
      city,
      uf,
    });
  },

  async setPhone({ commit }, { phone }) {
    commit("setPhone", { phone });
  },

  async getAllPlans({ commit, dispatch }): Promise<any> {
    const plans: any = await UserService.getAllPlans();
    return Promise.resolve(plans);
  },

  async newRegister({ commit, dispatch }, payload: any): Promise<any> {
    const registerResponse: any = await UserService.newRegister(payload);
    return Promise.resolve(registerResponse);
  },

  async updateRegister({ commit, dispatch }, payload: any): Promise<any> {
    const registerResponse: any = await UserService.updateRegister(payload);
    return Promise.resolve(registerResponse);
  },

  async newRegisterV2({ commit, dispatch }, payload: any): Promise<any> {
    const registerResponse: any = await UserService.newRegisterV2(payload);
    return Promise.resolve(registerResponse);
  },

  async updateRegisterV2({ commit, dispatch }, payload: any): Promise<any> {
    const registerResponse: any = await UserService.updateRegisterV2(payload);
    return Promise.resolve(registerResponse);
  },

  async getAddress({ commit, dispatch }, cepNumber: string): Promise<any> {
    const formatCep = cepNumber.replace("-", "");
    const addressResponse: ZipeCodeResponseType = await UserService.getAddress(
      formatCep
    );
    const {
      streetName: logradouro,
      neighborhood: bairro,
      zipCode: cep,
      prefix: ddd,
      complement: complemento,
      city: localidade,
      state: uf,
      referencePoint: referencia,
    } = addressResponse.content;
    return Promise.resolve({
      logradouro,
      bairro,
      cep,
      ddd,
      complemento,
      localidade,
      uf,
      referencia,
    });
  },

  async login({ commit, dispatch }, payload: any): Promise<any> {
    const loginResponse: any = await UserService.login(payload);
    return Promise.resolve(loginResponse);
  },

  async loginV2({ commit, dispatch }, payload: any): Promise<any> {
    const loginResponse: any = await UserService.loginV2(payload);
    return Promise.resolve(loginResponse);
  },

  async registerPortability({ commit, dispatch }, payload: any): Promise<any> {
    const portabilityResponse: any = await UserService.registerPortability(
      payload
    );
    return Promise.resolve(portabilityResponse);
  },

  async checkPortability({ commit, dispatch }, phone: string): Promise<any> {
    const checkPortResponse: any = await UserService.checkPortability(phone);
    return Promise.resolve(checkPortResponse);
  },

  async sendSmsPortability({ commit, dispatch }, payload: any): Promise<any> {
    const sendSmsResponse: any = await UserService.sendSmsPortability(payload);
    return Promise.resolve(sendSmsResponse);
  },

  async checkSmsPortability({ commit, dispatch }, sms: string): Promise<any> {
    const checkSmsResponse: any = await UserService.checkSmsPortability(sms);
    return Promise.resolve(checkSmsResponse);
  },

  async requestPortability(
    { commit, dispatch },
    productId: string
  ): Promise<any> {
    const requestPortResponse: any = await UserService.requestPortability(
      productId
    );
    return Promise.resolve(requestPortResponse);
  },

  async checkPhone(
    { commit, dispatch },
    { phone, customerId }: CheckPhoneType
  ): Promise<any> {
    const phoneResponse: any = await UserService.checkPhone({
      phone,
      customerId,
    });
    return Promise.resolve(phoneResponse);
  },

  async checkSms({ commit, dispatch }, sms: string): Promise<any> {
    const smsResponse: any = await UserService.checkSms(sms);
    return Promise.resolve(smsResponse.statusCode);
  },

  async createTokenCard({ commit, dispatch }, payload: any): Promise<any> {
    const tokenCard: any = await UserService.createTokenCard(payload);
    return Promise.resolve(tokenCard);
  },

  async saveCard({ commit, dispatch }, payload: any): Promise<any> {
    const tokenCard: any = await UserService.saveCard(payload);
    return Promise.resolve(tokenCard);
  },

  async getCart({ commit, dispatch }): Promise<any> {
    const cartResponse: any = await UserService.getCart();
    return Promise.resolve(cartResponse);
  },

  async checkBalance({ commit, dispatch }): Promise<any> {
    const balanceResponse: any = await UserService.checkBalance();
    return Promise.resolve(balanceResponse);
  },

  async getTerms({ commit, dispatch }): Promise<any> {
    const termsResponse: any = await UserService.getTerms();
    return Promise.resolve(termsResponse);
  },

  async sendTermsOnEmail({ commit, dispatch }, payload: any): Promise<any> {
    const sendTermsResponse: any = await UserService.sendTermsOnEmail(payload);
    return Promise.resolve(sendTermsResponse);
  },

  async step({ commit, dispatch }, query: any): Promise<any> {
    const cartResponse: any = await UserService.step(query);
    return Promise.resolve(cartResponse);
  },

  async getSubscription({ commit, dispatch }): Promise<any> {
    const subscriptionResponse: any = await UserService.getSubscription();
    return Promise.resolve(subscriptionResponse);
  },

  async checkout({ commit, dispatch }, payload: any): Promise<any> {
    const checkoutResponse: any = await UserService.checkout(payload);
    return Promise.resolve(checkoutResponse);
  },

  async checkoutV2({ commit, dispatch }, payload: any): Promise<any> {
    const checkoutResponse: any = await UserService.checkoutV2(payload);
    return Promise.resolve(checkoutResponse);
  },

  async resetPassword({ commit, dispatch }, payload: any): Promise<any> {
    const resetPasswordResponse: any = await UserService.resetPassword(payload);
    return Promise.resolve(resetPasswordResponse);
  },

  async validEmailCode({ commit, dispatch }, payload: any): Promise<any> {
    const sendEmailCodeResponse: any = await UserService.validEmailCode(
      payload
    );
    return Promise.resolve(sendEmailCodeResponse);
  },

  async changePassword({ commit, dispatch }, payload: any): Promise<any> {
    const changePwdResponse: any = await UserService.changePassword(payload);
    return Promise.resolve(changePwdResponse);
  },

  async checkCpf({ commit, dispatch }, cpf: string): Promise<any> {
    const cpfResponse: any = await UserService.checkCpf(cpf);
    return Promise.resolve(cpfResponse);
  },

  async checkCpfV2({ commit, dispatch }, cpf: string): Promise<any> {
    const cpfResponse: any = await UserService.checkCpfV2(cpf);
    return Promise.resolve(cpfResponse);
  },

  async validCpf({ commit, dispatch }, cpf: string): Promise<any> {
    const cpfResponse: any = await UserService.validCpf(cpf);
    return Promise.resolve(cpfResponse);
  },

  async forgotPwd({ commit, dispatch }, cpf: string): Promise<any> {
    const forgotPwdResponse: any = await UserService.forgotPwd(cpf);
    return Promise.resolve(forgotPwdResponse);
  },

  async checkAntifraud({ commit, dispatch }, payload: any): Promise<any> {
    const antifraudResponse: any = await UserService.checkAntifraud(
      payload.cpf,
      payload.phone
    );
    return Promise.resolve(antifraudResponse);
  },
  async checkCupom({ commit, dispatch }, payload: any): Promise<any> {
    const cupomResponse: any = await UserService.checkCupom(payload);
    return Promise.resolve(cupomResponse);
  },
  async validateCoupon({ commit, dispatch }): Promise<any> {
    const cupomResponse: any = await UserService.validateCoupon();
    return Promise.resolve(cupomResponse);
  },

  async getTokenChat({ commit, dispatch }): Promise<any> {
    const token: any = await UserService.getTokenChat();
    return Promise.resolve(token);
  },

  async getTokenM4U({ commit, dispatch }, phone: any): Promise<any> {
    const token: any = await UserService.getTokenM4U(phone);
    return Promise.resolve(token);
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
